import { useCallback, useId, useMemo, useState, startTransition, type FC } from "react";
import { ComboboxProvider } from "@ariakit/react";
import { useListProjects, type PlainProject } from "@/queries/project-queries";
import {
    Select,
    SelectContentCombobox,
    SelectItemCombobox,
    SelectTrigger,
    SelectValue,
} from "@/components/podkit/select/Select";
import { getRepoUrlFromInitializer } from "@/queries/environment-queries";

type TriggerProjectSelectorProps = {
    selectedProjectIds: string[];
    onSelectionChange: (projectIds: string[]) => void;
    disabled?: boolean;
};

export const TriggerProjectSelector: FC<TriggerProjectSelectorProps> = ({
    selectedProjectIds,
    onSelectionChange,
    disabled = false,
}) => {
    const selectId = useId();
    const [isSelectOpen, setIsSelectOpen] = useState(false);
    const [searchValue, setSearchValue] = useState("");

    const { data: projectsData, isPending: isLoadingProjects } = useListProjects({ enabled: true });

    const allProjects = useMemo(() => {
        if (!projectsData?.projects) return [];
        return projectsData.projects;
    }, [projectsData]);

    const availableProjects = useMemo(() => {
        const unselectedProjects = allProjects.filter((project): project is PlainProject =>
            Boolean(project?.id && !selectedProjectIds.includes(project.id)),
        );

        if (!searchValue) return unselectedProjects;

        const words = searchValue.toLowerCase().split(" ");
        return unselectedProjects.filter((project) => {
            const label = (project?.metadata?.name ?? "").toLowerCase();
            return words.every((word) => label.includes(word));
        });
    }, [allProjects, selectedProjectIds, searchValue]);

    const handleAddProject = useCallback(
        (projectId: string) => {
            if (!selectedProjectIds.includes(projectId)) {
                onSelectionChange([...selectedProjectIds, projectId]);
            }
            setIsSelectOpen(false);
        },
        [selectedProjectIds, onSelectionChange],
    );

    if (disabled) {
        return null;
    }

    return (
        <Select open={isSelectOpen} onOpenChange={setIsSelectOpen} onValueChange={handleAddProject} value="">
            <ComboboxProvider
                open={isSelectOpen}
                setOpen={setIsSelectOpen}
                resetValueOnHide
                includesBaseElement={false}
                setValue={(value) => {
                    startTransition(() => {
                        setSearchValue(value.toLowerCase());
                    });
                }}
            >
                <SelectTrigger id={selectId} loading={isLoadingProjects} className="w-fit">
                    <SelectValue placeholder="Add a project..." />
                </SelectTrigger>
                <SelectContentCombobox inputPlaceholder="Search projects..." className="w-128 max-w-[calc(100vw-1rem)]">
                    {availableProjects.length > 0 ? (
                        availableProjects.map((project) => (
                            <SelectItemCombobox
                                key={project.id}
                                value={project.id || ""}
                                className="w-128 cursor-pointer"
                            >
                                <div className="flex flex-col">
                                    <span className="font-medium">{project.metadata?.name}</span>
                                    <span className="text-xs text-content-muted">
                                        {project.initializer ? getRepoUrlFromInitializer(project.initializer) : "-"}
                                    </span>
                                </div>
                            </SelectItemCombobox>
                        ))
                    ) : (
                        <div className="px-2 py-1 text-sm text-content-muted">
                            {isLoadingProjects
                                ? "Loading projects..."
                                : searchValue
                                  ? "No projects found"
                                  : "All projects have been selected"}
                        </div>
                    )}
                </SelectContentCombobox>
            </ComboboxProvider>
        </Select>
    );
};
